/**
 * Defensive buildings that shoot back, plus the slow burn of construction damage
 * and the cleanup of anything reduced to rubble.
 */

import { alliedWith, areHostile, Team, UF } from '../types.ts';
import { EV, pushEvent } from '../events.ts';
import { recomputeManaCap, recomputePopCap, recomputeStorage, removeBuilding, type World } from '../world.ts';
import { buildingType } from '../../data/buildings.ts';
import { hostileMask } from '../spatial.ts';
import { Shot, spawnShot } from './projectiles.ts';
import { DamageType } from '../types.ts';

export function stepTowers(w: World, dt: number): void {
  const b = w.buildings;
  const u = w.units;

  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    const def = buildingType(b.typeId[i]);
    const atk = def.attack;
    if (!atk) continue;

    if (b.cooldown[i] > 0) {
      b.cooldown[i] -= dt;
      continue;
    }

    const cx = b.tx[i] + def.w / 2;
    const cy = b.ty[i] + def.h / 2;
    const myTeam = b.team[i];
    const mine = myTeam === Team.Player;
    const range = atk.range * (1 + (b.tier[i] - 1) * 0.12) * (mine ? w.mods.towerRange : 1);

    const j = u.slots.high === 0
      ? -1
      : w.spatial.nearest(cx, cy, range, u, (k) => {
          // The spatial hash is built once at the top of the tick, and towers
          // fire *after* melee and projectiles have killed people. Without a
          // liveness test a tower burns a full reload on a corpse — and worse,
          // `u.handle(j)` on a released slot mints the handle of whoever is
          // allocated into it next, so the shot silently re-homes onto a live
          // unit that may be one of your own.
          if (u.slots.alive[k] === 0 || u.hp[k] <= 0) return false;
          // A tower shoots what its owner is at war with — not simply what is
          // not its owner, which would have it firing on a bystander and, in a
          // 2v2, on the banner standing beside it.
          if (!areHostile(myTeam, u.team[k])) return false;
          if ((u.flags[k] & UF.Villager) !== 0) return false;
          if (!atk.hitsFliers && (u.flags[k] & UF.Flying) !== 0) return false;
          return true;
        }, hostileMask(myTeam));
    if (j < 0) continue;

    const damage = atk.damage * (1 + (b.tier[i] - 1) * 0.35) * (mine ? w.mods.towerDamage : 1);
    const kind =
      atk.dmgType === DamageType.Siege
        ? Shot.Siege
        : atk.dmgType === DamageType.Fire
          ? Shot.Magic
          : Shot.Arrow;

    spawnShot(w, kind, cx, cy, u.handle(j), u.x[j], u.y[j], damage, atk.dmgType, myTeam, atk.splash, -1);
    b.cooldown[i] = 1 / Math.max(0.05, atk.rate);
    pushEvent(w.events, EV.Shoot, cx, cy, kind, 1, myTeam, u.x[j] - cx, u.y[j] - cy);
  }
}

/** Sweep away anything that hit zero health this tick. */
export function stepStructureCleanup(w: World): void {
  const b = w.buildings;
  let changed = false;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.hp[i] > 0) continue;
    if (i === w.hearth) continue; // the run is already over; leave it standing for the camera
    removeBuilding(w, i, true);
    changed = true;
  }
  if (changed) {
    recomputePopCap(w);
    recomputeStorage(w);
    recomputeManaCap(w);
  }
}

/**
 * Live counts the UI, the director and the bots all read.
 *
 * All four of them, which was not true: `villagers` was the one field this
 * skipped, and the only other writer is `stepPopulation`, deep inside
 * `stepSettlement`. `loadWorld` calls this function *specifically* to repair the
 * counts before the first tick runs, so a freshly loaded settlement told
 * `bots.ts` it had no workforce at all — and a battle or a campaign world, which
 * never runs `stepSettlement`, was never going to be told otherwise.
 */
export function recount(w: World): void {
  const u = w.units;
  let player = 0;
  let enemy = 0;
  let villagers = 0;
  let idle = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (alliedWith(u.team[i], Team.Player)) {
      player++;
      if ((u.flags[i] & UF.Villager) !== 0) {
        villagers++;
        if (u.workSite[i] < 0) idle++;
      }
    } else if (areHostile(u.team[i], Team.Player)) {
      enemy++;
    }
  }
  w.counts.player = player;
  w.counts.enemy = enemy;
  w.counts.villagers = villagers;
  w.counts.idleVillagers = idle;
}
